// Zones et tarifs de livraison — source unique utilisée par le panier et la caisse.
//
// Le code postal fait foi : il est numérique et sans ambiguïté, contrairement au
// nom de ville, qui peut être écrit « Luik », « Liege », « Liége » ou comporter
// une faute de frappe. Le nom de ville ne sert que de secours quand le code
// postal n'est pas encore saisi ou n'est pas reconnu.

export const FRAIS_LIEGE = 5.90;
export const FRAIS_PERIPHERIE = 7.90;
export const FRAIS_HORS_LIEGE = 14.90;

// Montant minimum du panier (hors livraison) pour livrer en dehors de
// l'agglomération liégeoise.
export const MINIMUM_HORS_LIEGE = 75;

// Délai annoncé, repris de la page Livraison. Pour des produits frais et
// surgelés, « quand serai-je livré ? » est la question du client : il faut y
// répondre dans le panier et à la caisse, pas seulement sur une page à part.
export const DELAI_LIVRAISON = { fr: '24 à 48 h', en: '24 to 48 hours' };

export function delaiLivraison(langue = 'fr') {
  return DELAI_LIVRAISON[langue] || DELAI_LIVRAISON.fr;
}

// Codes postaux de la commune de Liège.
const CODES_LIEGE = ['4000', '4020', '4030', '4031', '4032'];

// Communes de la périphérie desservies au tarif intermédiaire.
// Cette liste reprend exactement les communes déjà annoncées dans les CGV ;
// elle doit être validée (et éventuellement étendue) par le commerçant.
const CODES_PERIPHERIE = [
  '4040', '4041', '4042',          // Herstal, Vottem, Liers
  '4050', '4051', '4052', '4053',  // Chaudfontaine, Vaux-sous-Chèvremont, Beaufays, Embourg
  '4100', '4101', '4102',          // Seraing, Jemeppe-sur-Meuse, Ougrée
  '4400',                          // Flémalle
  '4430', '4431', '4432',          // Ans, Loncin, Alleur
  '4460',                          // Grâce-Hollogne
  '4610',                          // Beyne-Heusay
];

const VILLES_LIEGE = ['liege', 'luik', 'bressoux', 'jupille', 'grivegnee', 'angleur', 'chenee'];

const VILLES_PERIPHERIE = [
  'seraing', 'jemeppe', 'ougree',
  'herstal', 'vottem', 'liers',
  'ans', 'loncin', 'alleur',
  'flemalle',
  'chaudfontaine', 'embourg', 'beaufays',
  'grace-hollogne', 'beyne', 'beyne-heusay',
];

/** Retire les accents et la casse pour comparer des noms de villes. */
export function normaliserTexte(valeur) {
  // On décompose puis on retire les signes diacritiques (plage Unicode
  // U+0300 à U+036F) : « Liege » et « Liège » deviennent identiques.
  return Array.from((valeur || '').normalize('NFD'))
    .filter((c) => {
      const code = c.codePointAt(0);
      return code < 0x0300 || code > 0x036f;
    })
    .join('')
    .toLowerCase()
    .trim();
}

/** Ne garde que les 4 chiffres d'un code postal belge. */
export function normaliserCodePostal(valeur) {
  const chiffres = (valeur || '').replace(/\D/g, '');
  return chiffres.length === 4 ? chiffres : '';
}

/**
 * Détermine la zone de livraison.
 * Renvoie 'liege', 'peripherie', 'hors_liege', ou null si l'on ne sait pas
 * encore (ni code postal valide, ni ville renseignée).
 */
export function determinerZone(codePostal, ville) {
  const code = normaliserCodePostal(codePostal);

  if (code) {
    if (CODES_LIEGE.includes(code)) return 'liege';
    if (CODES_PERIPHERIE.includes(code)) return 'peripherie';
    return 'hors_liege';
  }

  // Pas de code postal exploitable : on se rabat sur le nom de ville.
  // La comparaison porte sur des mots entiers : cherchée en sous-chaîne,
  // « ans » se retrouvait dans « Ransart » ou « Anseremme », facturées au
  // tarif périphérie et dispensées du minimum de commande.
  const nom = normaliserTexte(ville);
  if (!nom) return null;
  const mots = nom.split(/[^a-z-]+/).filter(Boolean);
  if (mots.some((m) => VILLES_LIEGE.includes(m))) return 'liege';
  if (mots.some((m) => VILLES_PERIPHERIE.includes(m))) return 'peripherie';
  return 'hors_liege';
}

/** Frais de livraison pour une zone, ou null si la zone est inconnue. */
export function fraisLivraison(zone) {
  switch (zone) {
    case 'liege': return FRAIS_LIEGE;
    case 'peripherie': return FRAIS_PERIPHERIE;
    case 'hors_liege': return FRAIS_HORS_LIEGE;
    default: return null;
  }
}

/** Libellé de la zone, affiché au client. */
export function libelleZone(zone, langue = 'fr') {
  const libelles = {
    liege: { fr: 'Liège', en: 'Liège' },
    peripherie: { fr: 'Périphérie de Liège', en: 'Liège outskirts' },
    hors_liege: { fr: 'Reste de la Belgique', en: 'Rest of Belgium' },
  };
  return libelles[zone] ? libelles[zone][langue] || libelles[zone].fr : '';
}

/** Vrai si la zone impose un montant minimum de commande. */
export function zoneAvecMinimum(zone) {
  return zone === 'hors_liege';
}

/**
 * Montant manquant pour atteindre le minimum de commande, ou 0 si le minimum
 * est atteint ou ne s'applique pas.
 */
export function montantManquant(zone, sousTotal) {
  if (!zoneAvecMinimum(zone)) return 0;
  return Math.max(0, MINIMUM_HORS_LIEGE - sousTotal);
}
